import { useState, useEffect } from 'react';
import { Github, Linkedin, Mail, ChevronDown } from 'lucide-react';
import { heroData } from '../data/portfolioData';

const ICON_MAP = { github: Github, linkedin: Linkedin, email: Mail };

export default function Hero() {
  const {  description, socialLinks, roles } = heroData;
  const [roleIdx, setRoleIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setRoleIdx(i => (i + 1) % roles.length), 2800);
    return () => clearInterval(t);
  }, [roles.length]);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ padding: '7rem 2rem 4rem' }}
    >
      {/* Animated grid background */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgba(124,109,250,0.04) 1px,transparent 1px),' +
            'linear-gradient(90deg,rgba(124,109,250,0.04) 1px,transparent 1px)',
          backgroundSize: '60px 60px',
          animation: 'grid-drift 20s linear infinite',
        }}
      />

      {/* Orbs */}
      <div
        className="absolute pointer-events-none"
        style={{ width: 500, height: 500, borderRadius: '50%', filter: 'blur(80px)', background: 'radial-gradient(circle,rgba(124,109,250,0.15),transparent 70%)', top: -100, left: -100, animation: 'float1 8s ease-in-out infinite' }}
      />
      <div
        className="absolute pointer-events-none"
        style={{ width: 400, height: 400, borderRadius: '50%', filter: 'blur(80px)', background: 'radial-gradient(circle,rgba(250,109,200,0.12),transparent 70%)', bottom: 0, right: 0, animation: 'float2 10s ease-in-out infinite' }}
      />
      <div
        className="absolute pointer-events-none"
        style={{ width: 300, height: 300, borderRadius: '50%', filter: 'blur(80px)', background: 'radial-gradient(circle,rgba(109,250,213,0.1),transparent 70%)', top: '40%', left: '60%', animation: 'float3 7s ease-in-out infinite' }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto text-center flex flex-col items-center gap-4">
        {/* Badge */}
        <div
          className="anim-fade-up-1 inline-flex items-center gap-2"
          style={{
            padding: '0.4rem 1.1rem', borderRadius: '100px',
            border: '1px solid rgba(124,109,250,0.3)',
            background: 'rgba(124,109,250,0.08)',
            fontSize: '0.75rem', color: 'var(--accent)',
            letterSpacing: '0.1em', textTransform: 'uppercase',
          }}
        >
          <span
            style={{
              width: 6, height: 6, borderRadius: '50%',
              background: 'var(--accent3)',
              animation: 'pulse-dot 2s ease-in-out infinite',
              display: 'inline-block',
            }}
          />
          Available for opportunities
        </div>

        {/* Name */}
        <h1
          className="anim-fade-up-2 font-bold leading-none"
          style={{ fontSize: 'clamp(3rem, 9vw, 7rem)', letterSpacing: '-0.03em' }}
        >
          <span className="block" style={{ color: 'var(--text)' }}>Er. Guptaraj</span>
          <span className="block gradient-text">Shrestha</span>
        </h1>

        {/* Roles */}
        <div
          className="anim-fade-up-3 overflow-hidden"
          style={{ height: '2.2rem', fontFamily: "'Space Mono', monospace", fontSize: '1.05rem', color: 'var(--accent3)' }}
        >
          <div
            style={{
              transition: 'transform 0.5s cubic-bezier(0.16,1,0.3,1)',
              transform: `translateY(-${roleIdx * 2.2}rem)`,
            }}
          >
            {roles.map(r => (
              <div key={r} style={{ height: '2.2rem', lineHeight: '2.2rem' }}>{r}</div>
            ))}
          </div>
        </div>

        {/* Description */}
        <p
          className="anim-fade-up-4 max-w-[42rem]"
          style={{ fontSize: '1.05rem', color: 'var(--text2)', lineHeight: 1.75 }}
        >
          {description}
        </p>

        {/* CTAs */}
        <div className="anim-fade-up-5 flex flex-wrap gap-3 justify-center">
          <a
            href="#projects"
            style={{
              padding: '0.85rem 2rem', borderRadius: '100px', fontSize: '0.9rem', fontWeight: 600,
              background: 'linear-gradient(135deg, var(--accent), var(--accent2))',
              color: 'white', textDecoration: 'none',
              boxShadow: '0 0 30px rgba(124,109,250,0.35)',
              transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'none',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 0 50px rgba(124,109,250,0.55)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 0 30px rgba(124,109,250,0.35)'; }}
          >
            View My Work
          </a>
          <a
            href="#contact"
            style={{
              padding: '0.85rem 2rem', borderRadius: '100px', fontSize: '0.9rem', fontWeight: 600,
              border: '1px solid var(--border)', color: 'var(--text2)',
              textDecoration: 'none', background: 'transparent',
              transition: 'all 0.3s', cursor: 'none',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.background = 'rgba(124,109,250,0.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.background = 'transparent'; }}
          >
            Get in Touch
          </a>
        </div>

        {/* Social links */}
        <div className="anim-fade-up-6 flex gap-4 mt-2">
          {socialLinks.map(({ type, url }) => {
            const Icon = ICON_MAP[type];
            return (
              <a
                key={type} href={url} target="_blank" rel="noopener noreferrer"
                style={{
                  width: 44, height: 44, borderRadius: '50%',
                  border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text2)', textDecoration: 'none',
                  background: 'var(--card)', cursor: 'none',
                  transition: 'all 0.3s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.background = 'rgba(124,109,250,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.transform = ''; e.currentTarget.style.background = 'var(--card)'; }}
                aria-label={type}
              >
                <Icon size={18} />
              </a>
            );
          })}
        </div>
      </div>

      {/* Scroll indicator */}
      <a
        href="#about"
        className="absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        style={{ color: 'var(--text2)', fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', textDecoration: 'none', cursor: 'none' }}
      >
        <div style={{ width: 1, height: 44, background: 'linear-gradient(to bottom, var(--text2), transparent)', animation: 'scroll-pulse 2s ease-in-out infinite' }} />
        <span>Scroll</span>
        <ChevronDown size={14} />
      </a>
    </section>
  );
}
